import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "#app/notifications";
import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { InputDuration } from "#components/atoms/InputDuration/InputDuration";
import { Switch } from "#components/atoms/Switch/Switch";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { DropdownMenu } from "#components/molecules/DropdownMenu/DropdownMenu";
import {
  SidePanelContent,
  SidePanelFooter,
  SidePanelHeader,
} from "#components/molecules/SidePanel/SidePanel";
import { formatDurationLabel } from "#modules/onboardingV2/domain/durationFormula";
import { hasDisplayableGain } from "#modules/optimizations/domain/getOptimizationGain";
import {
  getOptimizationSubtitle,
  getOptimizationTitle,
} from "#modules/optimizations/domain/optimizationCardContent";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { CancelOptimizationModal } from "#modules/optimizations/view/CancelOptimizationModal";
import { useExtractOptimizationLine } from "#modules/optimizations/view/useExtractOptimizationLine";
import { useToggleOptimization } from "#modules/optimizations/view/useToggleOptimization";
import { useUpdateMachineTimes } from "#modules/optimizations/view/useUpdateMachineTimes";
import { formatPartDimensions } from "#modules/parts/domain/formatPartDimensions";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";
import {
  sumMachineTimesOptimizationGain,
  distributeTimeProRata,
  formatPrice,
  getOptimizationMachineProcessSteps,
  minutesToTimeParts,
  sumMachineTimes,
  timePartsToMinutes,
  TimeParts,
} from "shared";

type Props = {
  optimization: OptimizationDto;
  onBack: () => void;
  onRequestBack: () => void;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

const TABLE_COLUMNS = "minmax(0, 1fr) 71px 118px 118px 32px";

const sumMinutes = (values: Array<number | null>) =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0);

const DURATION_EXCEEDS_EXISTING_TOAST_ID = "optimization-duration-exceeds-existing";

const isSameTimeParts = (left: TimeParts, right: TimeParts): boolean =>
  left.hours === right.hours && left.minutes === right.minutes && left.seconds === right.seconds;

const resolveLineTimes = (
  currentTimes: Array<number | null>,
  edited: TimeParts,
  initial: TimeParts,
) =>
  isSameTimeParts(edited, initial)
    ? currentTimes.map((time) => time ?? 0)
    : distributeTimeProRata({
        currentTimes,
        newTotal: timePartsToMinutes(edited),
      });

const DurationField = ({
  label,
  initialValue,
  value,
  onChange,
  onReset,
}: {
  label: string;
  initialValue: TimeParts;
  value: TimeParts;
  onChange: (update: (current: TimeParts) => TimeParts) => void;
  onReset: () => void;
}) => {
  const { t } = useTranslation();
  const [inputKey, setInputKey] = useState(0);

  const showInitialLabel = !isSameTimeParts(value, initialValue);

  const handleCommit = (next: TimeParts) => {
    if (timePartsToMinutes(next) <= timePartsToMinutes(initialValue)) {
      return;
    }

    showErrorToast({
      id: DURATION_EXCEEDS_EXISTING_TOAST_ID,
      title: t("optimizations.detail.durationExceedsInitial"),
    });
    return false;
  };

  return (
    <Stack gap={spacing[1]} style={{ flex: 1, minWidth: 0 }}>
      <Text size="small" type="medium">
        {label}
      </Text>
      <InputDuration
        key={inputKey}
        hours={value.hours}
        minutes={value.minutes}
        seconds={value.seconds}
        icon={<Icon name="rotateLeft" size={16} color={colors.icon.secondary} />}
        onIconClick={() => {
          onReset();
          setInputKey((current) => current + 1);
        }}
        iconLabel={t("optimizations.detail.reset")}
        onHoursChange={(hours) => onChange((current) => ({ ...current, hours }))}
        onMinutesChange={(minutes) => onChange((current) => ({ ...current, minutes }))}
        onSecondsChange={(seconds) => onChange((current) => ({ ...current, seconds }))}
        onCommit={handleCommit}
      />
      {showInitialLabel ? (
        <Text size="tiny" color={colors.text.secondaryDefault}>
          {t("optimizations.detail.initialDuration", {
            duration: formatDurationLabel(initialValue),
          })}
        </Text>
      ) : null}
    </Stack>
  );
};

const DurationCell = ({ minutes }: { minutes: number | null }) => (
  <Text size="small" textAlign="right">
    {formatDurationLabel(minutesToTimeParts(minutes))}
  </Text>
);

export const ProcessOptimizationDetail = ({
  optimization,
  onBack,
  onRequestBack,
  onUnsavedChangesChange,
}: Props) => {
  const { t } = useTranslation();

  const { toggleOptimizationAsync, isPending: isTogglePending } = useToggleOptimization({
    quoteRequestId: optimization.quoteRequestId,
  });
  const { updateMachineTimesAsync, isPending: isUpdatePending } = useUpdateMachineTimes({
    quoteRequestId: optimization.quoteRequestId,
  });
  const { requestExtract, confirmExtract, cancelExtract, isConfirmOpen, isExtractPending } =
    useExtractOptimizationLine({ optimization, onCancelled: onBack });

  const applyToQuoteId = useId();
  const metadata = optimization.machineTimesOptimizationMetadata;
  const rows = getOptimizationMachineProcessSteps(optimization);
  const initialTimes = metadata
    ? sumMachineTimes({
        lines: optimization.quoteRequestLines,
        industrialProcess: metadata.companyMachine.machine.industrialProcess,
        companyMachineId: metadata.companyMachineId,
      })
    : { machinePreparationTime: 0, otherMachineTime: 0 };
  const initialPreparationTotalTime = minutesToTimeParts(initialTimes.machinePreparationTime);
  const initialFabricationTotalTime = minutesToTimeParts(initialTimes.otherMachineTime);
  const savedOptimizationPreparationTotalTime = minutesToTimeParts(
    metadata?.machinePreparationTime ?? 0,
  );
  const savedOptimizationFabricationTotalTime = minutesToTimeParts(metadata?.otherMachineTime ?? 0);
  const [editingPreparationTotalTime, setEditingPreparationTotalTime] = useState(
    savedOptimizationPreparationTotalTime,
  );
  const [editingFabricationTotalTime, setEditingFabricationTotalTime] = useState(
    savedOptimizationFabricationTotalTime,
  );
  const [previousSavedPreparationTotalTime, setPreviousSavedPreparationTotalTime] = useState(
    savedOptimizationPreparationTotalTime,
  );
  const [previousSavedFabricationTotalTime, setPreviousSavedFabricationTotalTime] = useState(
    savedOptimizationFabricationTotalTime,
  );
  const [isAppliedToQuote, setIsAppliedToQuote] = useState(optimization.enabled);

  useEffect(() => {
    if (
      !isSameTimeParts(savedOptimizationPreparationTotalTime, previousSavedPreparationTotalTime)
    ) {
      setPreviousSavedPreparationTotalTime(savedOptimizationPreparationTotalTime);
      setEditingPreparationTotalTime(savedOptimizationPreparationTotalTime);
    }

    if (
      !isSameTimeParts(savedOptimizationFabricationTotalTime, previousSavedFabricationTotalTime)
    ) {
      setPreviousSavedFabricationTotalTime(savedOptimizationFabricationTotalTime);
      setEditingFabricationTotalTime(savedOptimizationFabricationTotalTime);
    }
  }, [
    savedOptimizationPreparationTotalTime,
    savedOptimizationFabricationTotalTime,
    previousSavedPreparationTotalTime,
    previousSavedFabricationTotalTime,
  ]);

  const preparationTimes = resolveLineTimes(
    rows.map((row) => row.step.machinePreparationTime),
    editingPreparationTotalTime,
    initialPreparationTotalTime,
  );
  const fabricationTimes = resolveLineTimes(
    rows.map((row) => row.step.otherMachineTime),
    editingFabricationTotalTime,
    initialFabricationTotalTime,
  );
  const preparationTotalTime = sumMinutes(preparationTimes);
  const fabricationTotalTime = sumMinutes(fabricationTimes);
  const estimatedGain = sumMachineTimesOptimizationGain({
    lines: rows.map((row) => ({
      quantity: row.line.quantity,
      quantityInAssembly: row.line.part.quantityInAssembly,
      reworkRatio: row.line.reworkRatio ?? null,
      processStep: row.step,
    })),
    optimizedMachineTimes: new Map(
      rows.map((row, index) => [
        row.step.id,
        {
          machinePreparationTime: preparationTimes[index] ?? 0,
          otherMachineTime: fabricationTimes[index] ?? 0,
        },
      ]),
    ),
  });

  const hasEditedDurations =
    !isSameTimeParts(editingPreparationTotalTime, savedOptimizationPreparationTotalTime) ||
    !isSameTimeParts(editingFabricationTotalTime, savedOptimizationFabricationTotalTime);
  const hasGain = hasDisplayableGain(estimatedGain);
  const isEffectivelyAppliedToQuote = isAppliedToQuote && hasGain;
  const hasUnsavedChanges =
    hasEditedDurations || isEffectivelyAppliedToQuote !== optimization.enabled;

  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  const onFinish = async () => {
    await Promise.all([
      metadata && hasEditedDurations
        ? updateMachineTimesAsync({
            optimizationId: optimization.id,
            machinePreparationTime: timePartsToMinutes(editingPreparationTotalTime),
            otherMachineTime: timePartsToMinutes(editingFabricationTotalTime),
          })
        : Promise.resolve(),
      isEffectivelyAppliedToQuote !== optimization.enabled
        ? toggleOptimizationAsync({
            optimizationId: optimization.id,
            enabled: isEffectivelyAppliedToQuote,
          })
        : Promise.resolve(),
    ]);

    onBack();
  };

  const autoApplyToQuote = (next: TimeParts, initial: TimeParts) => {
    if (isAppliedToQuote) {
      return;
    }
    if (!isSameTimeParts(next, initial)) {
      setIsAppliedToQuote(true);
    }
  };

  const onChangePreparationTotalTime = (update: (current: TimeParts) => TimeParts) => {
    const next = update(editingPreparationTotalTime);
    setEditingPreparationTotalTime(next);
    autoApplyToQuote(next, initialPreparationTotalTime);
  };

  const onChangeFabricationTotalTime = (update: (current: TimeParts) => TimeParts) => {
    const next = update(editingFabricationTotalTime);
    setEditingFabricationTotalTime(next);
    autoApplyToQuote(next, initialFabricationTotalTime);
  };

  return (
    <>
      <SidePanelHeader
        title={getOptimizationTitle(optimization, t)}
        subtitle={getOptimizationSubtitle(optimization, t)}
        onBack={onRequestBack}
        backText={t("optimizations.detail.back")}
        onClose={onRequestBack}
        closeText={t("common.close")}
      />
      <SidePanelContent>
        <Stack gap={spacing[8]}>
          <Stack gap={spacing[3]}>
            <Text size="base">{t("optimizations.detail.description")}</Text>
            <Flex gap={spacing[6]} align="flex-start">
              <DurationField
                label={t("optimizations.detail.preparationTotalTime")}
                value={editingPreparationTotalTime}
                initialValue={initialPreparationTotalTime}
                onChange={onChangePreparationTotalTime}
                onReset={() => setEditingPreparationTotalTime(initialPreparationTotalTime)}
              />
              <DurationField
                label={t("optimizations.detail.fabricationTotalTime")}
                value={editingFabricationTotalTime}
                initialValue={initialFabricationTotalTime}
                onChange={onChangeFabricationTotalTime}
                onReset={() => setEditingFabricationTotalTime(initialFabricationTotalTime)}
              />
            </Flex>
          </Stack>
          <Box
            bg={colors.bg.elevation.tertiary}
            px={spacing[6]}
            pt={spacing[5]}
            pb={spacing[6]}
            style={{ borderRadius: radius.md }}
          >
            <Stack gap={spacing[5]}>
              <Title size="tiny">{t("optimizations.detail.distribution")}</Title>
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: TABLE_COLUMNS,
                  alignItems: "center",
                }}
              >
                <Box px={spacing[2]} py={spacing[2]}>
                  <Text size="small" type="medium">
                    {t("optimizations.detail.part")}
                  </Text>
                </Box>
                <Box px={spacing[2]} py={spacing[2]}>
                  <Text size="small" type="medium" textAlign="right">
                    {t("optimizations.detail.quantity")}
                  </Text>
                </Box>
                <Box px={spacing[2]} py={spacing[2]}>
                  <Text size="small" type="medium" textAlign="right">
                    {t("optimizations.detail.preparation")}
                  </Text>
                </Box>
                <Box px={spacing[2]} py={spacing[2]}>
                  <Text size="small" type="medium" textAlign="right">
                    {t("optimizations.detail.fabrication")}
                  </Text>
                </Box>
                <span />
                {rows.map(({ line, step }, index) => {
                  const typeLabel = line.part.type ? t(`part.type.${line.part.type}`) : "";
                  const dimensions = formatPartDimensions(line.part);

                  return (
                    <Box key={step.id} style={{ display: "contents" }}>
                      <Box style={{ gridColumn: "1 / -1" }}>
                        <Divider />
                      </Box>
                      <Flex align="center" gap={spacing[2]} pl={spacing[2]} py={spacing[2]}>
                        <ThumbnailOverlay part={line.part} size={{ width: 48, height: 48 }} />
                        <Stack gap={spacing[1]} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="small" type="medium" truncate>
                            {line.part.name}
                          </Text>
                          {typeLabel || dimensions ? (
                            <Flex align="center" gap={spacing[1.5]} style={{ minWidth: 0 }}>
                              {typeLabel ? (
                                <Text size="tiny" color={colors.text.secondaryDefault}>
                                  {typeLabel}
                                </Text>
                              ) : null}
                              {typeLabel && dimensions ? (
                                <Divider
                                  orientation="vertical"
                                  style={{ height: spacing[4], alignSelf: "center" }}
                                />
                              ) : null}
                              {dimensions ? (
                                <Text size="tiny" color={colors.text.secondaryDefault} truncate>
                                  {dimensions}
                                </Text>
                              ) : null}
                            </Flex>
                          ) : null}
                        </Stack>
                      </Flex>
                      <Box px={spacing[2]}>
                        <Text size="small" textAlign="right">
                          {line.quantity * (line.part.quantityInAssembly ?? 1)}
                        </Text>
                      </Box>
                      <Box px={spacing[2]}>
                        <DurationCell minutes={preparationTimes[index] ?? null} />
                      </Box>
                      <Box px={spacing[2]}>
                        <DurationCell minutes={fabricationTimes[index] ?? null} />
                      </Box>
                      <DropdownMenu position="bottom-end">
                        <DropdownMenu.Target>
                          <IconButton
                            variant="transparent"
                            size="sm"
                            aria-label={t("optimizations.detail.rowActions")}
                          >
                            <Icon name="ellipsis" size={16} />
                          </IconButton>
                        </DropdownMenu.Target>
                        <DropdownMenu.Dropdown>
                          <DropdownMenu.Item
                            leftSection={
                              <Icon name="layers3" size={16} color={colors.icon.primaryDefault} />
                            }
                            disabled={isExtractPending}
                            onClick={() => requestExtract(line.id)}
                          >
                            {t("optimizations.detail.extractFromNesting")}
                          </DropdownMenu.Item>
                        </DropdownMenu.Dropdown>
                      </DropdownMenu>
                    </Box>
                  );
                })}
                <Box style={{ gridColumn: "1 / -1" }}>
                  <Divider />
                </Box>
                <span />
                <span />
                <Box px={spacing[2]} pt={spacing[2]}>
                  <Text size="small" type="medium" textAlign="right">
                    {formatDurationLabel(minutesToTimeParts(preparationTotalTime))}
                  </Text>
                </Box>
                <Box px={spacing[2]} pt={spacing[2]}>
                  <Text size="small" type="medium" textAlign="right">
                    {formatDurationLabel(minutesToTimeParts(fabricationTotalTime))}
                  </Text>
                </Box>
                <span />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </SidePanelContent>
      <SidePanelFooter>
        <Flex align="center" justify="space-between" w="100%" gap={spacing[4]}>
          <Text
            size="base"
            type="medium"
            color={hasGain ? colors.text.success : colors.text.secondaryDefault}
          >
            {t("optimizations.detail.estimatedGain", {
              gain: formatPrice(estimatedGain, " €", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            })}
          </Text>
          <Flex align="center" gap={spacing[6]} style={{ flexShrink: 0 }}>
            <Flex align="center" gap={spacing[2]}>
              <label htmlFor={applyToQuoteId} style={{ cursor: "pointer" }}>
                <Text size="small" color={colors.text.secondaryDefault} component="span">
                  {t("optimizations.detail.applyToQuote")}
                </Text>
              </label>
              <Switch
                id={applyToQuoteId}
                checked={isEffectivelyAppliedToQuote}
                disabled={!hasGain}
                onChange={setIsAppliedToQuote}
              />
            </Flex>
            <Button
              variant="primary"
              size="lg"
              onClick={onFinish}
              disabled={isTogglePending || isUpdatePending}
            >
              {t("optimizations.done")}
            </Button>
          </Flex>
        </Flex>
      </SidePanelFooter>
      <CancelOptimizationModal
        opened={isConfirmOpen}
        isConfirming={isExtractPending}
        onConfirm={confirmExtract}
        onKeepEditing={cancelExtract}
      />
    </>
  );
};
